"use client";

import { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Camera } from "@/schemas/camera";
import {
  usePostAssignCameraToBranch,
  usePostUnassignCameraFromBranch,
  usePostSetCameraFlags,
} from "@/api/pizza-checker/camera/camera";
import { toast } from "sonner";
import { Link, Unlink, Loader2, RotateCw } from "lucide-react";
import { useCameraContext } from "@/context/camera-context";
import { useBranch, Branch } from "@/context/branch-context";
import { useLocalization } from "@/context/localization-context";

interface CameraActionsProps {
  camera: Camera;
}

export const CameraActions = ({ camera }: CameraActionsProps) => {
  const { t } = useLocalization();
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [unassignDialogOpen, setUnassignDialogOpen] = useState(false);
  const [rebootDialogOpen, setRebootDialogOpen] = useState(false);
  const [selectedBranchUuid, setSelectedBranchUuid] = useState("");

  const { refetchCameras } = useCameraContext();

  // Use branch context instead of direct API call
  const { branches, branchesLoading } = useBranch();
  const assignMutation = usePostAssignCameraToBranch();
  const unassignMutation = usePostUnassignCameraFromBranch();
  const setFlagsMutation = usePostSetCameraFlags();

  const handleAssign = async () => {
    if (!selectedBranchUuid) {
      toast.error(t("cameras.assign.selectBranchFirst"));
      return;
    }

    try {
      await assignMutation.mutateAsync({
        data: {
          device_id: camera.id,
          branch_uuid: selectedBranchUuid,
        },
      });

      toast.success(t("cameras.assign.success", { name: camera.name }));
      setAssignDialogOpen(false);
      setSelectedBranchUuid("");
      refetchCameras?.();
    } catch (error) {
      toast.error(t("cameras.assign.failed"));
      console.error("Assignment error:", error);
    }
  };

  const handleUnassign = async () => {
    try {
      await unassignMutation.mutateAsync({
        data: {
          device_id: camera.id,
        },
      });

      toast.success(t("cameras.unassign.success", { name: camera.name }));
      setUnassignDialogOpen(false);
      refetchCameras?.();
    } catch (error) {
      toast.error(t("cameras.unassign.failed"));
      console.error("Unassignment error:", error);
    }
  };

  const handleReboot = async () => {
    try {
      await setFlagsMutation.mutateAsync({
        data: {
          device_id: camera.id,
          restart_camera: true,
          restart_service: null,
        },
      });

      toast.success(t("cameras.reboot.success", { name: camera.name }));
      setRebootDialogOpen(false);
    } catch (error) {
      toast.error(t("cameras.reboot.failed"));
      console.error("Reboot error:", error);
    }
  };

  return (
    <div className="flex items-center gap-2">
      {/* Assignment Status */}
      {/* <Badge variant={camera.assigned ? "default" : "secondary"}>
        {camera.assigned ? "Assigned" : "Unassigned"}
      </Badge> */}

      {/* Assign Button */}
      {!camera.assigned && (
        <Dialog
          open={assignDialogOpen}
          onOpenChange={(open) => {
            setAssignDialogOpen(open);
            if (!open) {
              setSelectedBranchUuid("");
            }
          }}
        >
          <DialogTrigger asChild>
            <Button variant="outline" size="sm">
              <Link className="h-4 w-4 mr-1" />
              {t("cameras.assign.button")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("cameras.assign.title")}</DialogTitle>
              <DialogDescription>
                {t("cameras.assign.description", { name: camera.name })}
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-4">
              <div className="grid grid-cols-4 items-center gap-4">
                <Label htmlFor="branch" className="text-right">
                  {t("common.branch")}
                </Label>
                <div className="col-span-3">
                  <Select
                    value={selectedBranchUuid}
                    onValueChange={setSelectedBranchUuid}
                    disabled={branchesLoading}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          branchesLoading
                            ? t("cameras.assign.loadingBranches")
                            : !branches || branches.length === 0
                              ? t("cameras.assign.noBranches")
                              : t("cameras.assign.selectBranch")
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {(!branches || branches.length === 0) &&
                      !branchesLoading ? (
                        <div className="p-2 text-sm text-muted-foreground">
                          {t("cameras.assign.noBranches")}
                        </div>
                      ) : (
                        branches?.map((branch: Branch) => (
                          <SelectItem
                            key={branch.bb_branch_id}
                            value={branch.bb_branch_id}
                          >
                            {branch.name}
                          </SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button
                type="submit"
                onClick={handleAssign}
                disabled={
                  assignMutation.isPending ||
                  !selectedBranchUuid ||
                  !branches ||
                  branches.length === 0
                }
              >
                {assignMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("cameras.assign.submit")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Unassign Button */}
      {camera.assigned && (
        <Dialog open={unassignDialogOpen} onOpenChange={setUnassignDialogOpen}>
          <DialogTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 hover:bg-red-500/10"
            >
              <Unlink className="h-4 w-4 mr-1" />
              {t("cameras.unassign.button")}
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>{t("cameras.unassign.title")}</DialogTitle>
              <DialogDescription>
                {t("cameras.unassign.description", { name: camera.name })}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => setUnassignDialogOpen(false)}
              >
                {t("common.cancel")}
              </Button>
              <Button
                className="bg-red-500/10 hover:bg-red-500/20 text-red-700 dark:text-red-300 border-red-200 dark:border-red-800 backdrop-blur-sm"
                onClick={handleUnassign}
                disabled={unassignMutation.isPending}
              >
                {unassignMutation.isPending && (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                )}
                {t("cameras.unassign.submit")}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Reboot Button */}
      <Dialog open={rebootDialogOpen} onOpenChange={setRebootDialogOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm">
            <RotateCw className="h-4 w-4 mr-1" />
            {t("cameras.reboot.button")}
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t("cameras.reboot.title")}</DialogTitle>
            <DialogDescription>
              {t("cameras.reboot.description", { name: camera.name })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setRebootDialogOpen(false)}
            >
              {t("common.cancel")}
            </Button>
            <Button
              onClick={handleReboot}
              disabled={setFlagsMutation.isPending}
            >
              {setFlagsMutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {t("cameras.reboot.submit")}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
